/**
 * Market Intelligence's demo state: seeded once per tenant from a fixture,
 * then mutated in place as the viewer adds competitors and tracked
 * competitions by hand or via CSV import — "client-side parse into the mock
 * client" (the story's acceptance criteria) means this state *is* the mock
 * backend for the module, not a re-derivation on every read.
 *
 * A tracked competition's status is never stored: it is always computed from
 * its dates against "today", the same clock every other module reads, so a
 * competition can move from upcoming to live to closing soon to closed
 * without anything here mutating it.
 */

import type {
  ActivityType,
  CategoryPricePoint,
  Competitor,
  CompetitorFrequency,
  MarketActivitySummary,
  MarketObservation,
  MarketPricingSummary,
  ObservationConfidence,
  ObservationSource,
  PrizeTrendPoint,
  TrackedCompetition,
  TrackedCompetitionStatus,
} from '@/api/market'
import { addDays, dayCount, type DateWindow } from '@/lib/filters'

import type { MarketFixture } from './fixtures'

export interface CompetitorRecord {
  id: string
  name: string
  domain: string
  brand: string
  frequency: CompetitorFrequency
  typicalTicketPrice: number
  prizeCategories: string[]
  trackedSince: string
}

export interface TrackedCompetitionRecord {
  id: string
  competitorId: string
  prize: string
  category: string
  ticketPrice: number
  maxEntries: number
  instantWins: number
  startDate: string
  endDate: string
  promotionalMessaging: string
}

export interface ObservationRecord {
  id: string
  competitorId: string | null
  trackedCompetitionId: string | null
  activityType: ActivityType
  fact: string
  occurredAt: string
  source: ObservationSource
  confidence: ObservationConfidence
}

export interface MarketState {
  competitors: CompetitorRecord[]
  trackedCompetitions: TrackedCompetitionRecord[]
  observations: ObservationRecord[]
}

export function seedState(fixture: MarketFixture, today: string): MarketState {
  return {
    competitors: fixture.competitors.map((competitor) => ({
      id: competitor.id,
      name: competitor.name,
      domain: competitor.domain,
      brand: competitor.brand,
      frequency: competitor.frequency,
      typicalTicketPrice: competitor.typicalTicketPrice,
      prizeCategories: [...competitor.prizeCategories],
      trackedSince: addDays(today, -competitor.trackedSinceDaysAgo),
    })),
    trackedCompetitions: fixture.trackedCompetitions.map((tracked) => ({
      id: tracked.id,
      competitorId: tracked.competitorId,
      prize: tracked.prize,
      category: tracked.category,
      ticketPrice: tracked.ticketPrice,
      maxEntries: tracked.maxEntries,
      instantWins: tracked.instantWins,
      startDate: addDays(today, tracked.startsInDays),
      endDate: addDays(today, tracked.startsInDays + tracked.durationDays - 1),
      promotionalMessaging: tracked.promotionalMessaging,
    })),
    observations: fixture.observations.map((observation) => ({
      id: observation.id,
      competitorId: observation.competitorId,
      trackedCompetitionId: observation.trackedCompetitionId,
      activityType: observation.activityType,
      fact: observation.fact,
      occurredAt: addDays(today, -observation.occurredDaysAgo),
      source: observation.source,
      confidence: observation.confidence,
    })),
  }
}

let counter = 0

/** A fresh id for a record added this session. Not seed data, so it need not be stable across reloads. */
export function nextId(prefix: string): string {
  counter += 1
  return `${prefix}_${counter}_${Math.random().toString(36).slice(2, 7)}`
}

/** A competition closes soon inside its last three days on sale. */
const CLOSING_SOON_DAYS = 3

export function statusFor(startDate: string, endDate: string, today: string): TrackedCompetitionStatus {
  if (endDate < today) return 'closed'
  if (startDate > today) return 'upcoming'
  return dayCount(today, endDate) - 1 <= CLOSING_SOON_DAYS ? 'closing_soon' : 'live'
}

export function buildCompetitors(state: MarketState, today: string): Competitor[] {
  return state.competitors.map((competitor) => {
    const activeCompetitions = state.trackedCompetitions.filter(
      (tracked) => tracked.competitorId === competitor.id && statusFor(tracked.startDate, tracked.endDate, today) !== 'closed',
    ).length
    return {
      id: competitor.id,
      name: competitor.name,
      domain: competitor.domain,
      brand: competitor.brand,
      activeCompetitions,
      typicalTicketPrice: competitor.typicalTicketPrice,
      frequency: competitor.frequency,
      prizeCategories: competitor.prizeCategories,
      trackedSince: competitor.trackedSince,
      drillDown: { path: `/market/competitors/${competitor.id}` },
    }
  })
}

export function buildTrackedCompetitions(state: MarketState, today: string): TrackedCompetition[] {
  return state.trackedCompetitions.map((tracked) => {
    const competitor = state.competitors.find((candidate) => candidate.id === tracked.competitorId)
    return {
      id: tracked.id,
      competitorId: tracked.competitorId,
      competitorName: competitor?.name ?? 'Unknown competitor',
      prize: tracked.prize,
      category: tracked.category,
      ticketPrice: tracked.ticketPrice,
      maxEntries: tracked.maxEntries,
      instantWins: tracked.instantWins,
      startDate: tracked.startDate,
      endDate: tracked.endDate,
      promotionalMessaging: tracked.promotionalMessaging,
      status: statusFor(tracked.startDate, tracked.endDate, today),
      drillDown: { path: `/market/competitors/${tracked.competitorId}` },
    }
  })
}

export function buildObservations(state: MarketState): MarketObservation[] {
  return [...state.observations]
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt) || b.id.localeCompare(a.id))
    .map((observation) => {
      const competitor = observation.competitorId
        ? state.competitors.find((candidate) => candidate.id === observation.competitorId)
        : undefined
      return {
        id: observation.id,
        competitorId: observation.competitorId,
        competitorName: competitor?.name ?? null,
        trackedCompetitionId: observation.trackedCompetitionId,
        activityType: observation.activityType,
        fact: observation.fact,
        occurredAt: observation.occurredAt,
        source: observation.source,
        confidence: observation.confidence,
        drillDown: { path: competitor ? `/market/competitors/${competitor.id}` : '/market/activity' },
      }
    })
}

export function inWindow(observations: readonly MarketObservation[], window: DateWindow): MarketObservation[] {
  return observations.filter((observation) => observation.occurredAt >= window.from && observation.occurredAt <= window.to)
}

export function buildActivitySummary(
  windowed: readonly MarketObservation[],
  tracked: readonly TrackedCompetition[],
): MarketActivitySummary {
  return {
    newLaunches: windowed.filter((observation) => observation.activityType === 'launch').length,
    closingSoon: tracked.filter((competition) => competition.status === 'closing_soon').length,
    priceChanges: windowed.filter((observation) => observation.activityType === 'price_change').length,
    categoryTrends: windowed.filter((observation) => observation.activityType === 'category_trend').length,
  }
}

export function buildPricingSummary(tracked: readonly TrackedCompetition[]): MarketPricingSummary {
  const current = tracked.filter((competition) => competition.status !== 'closed')
  const categories = new Map<string, TrackedCompetition[]>()
  for (const competition of current) {
    const list = categories.get(competition.category) ?? []
    list.push(competition)
    categories.set(competition.category, list)
  }

  const byCategory: CategoryPricePoint[] = [...categories.entries()]
    .map(([category, entries]) => ({
      category,
      trackedCount: entries.length,
      averageTicketPrice: average(entries.map((entry) => entry.ticketPrice)),
      averageMaxEntries: average(entries.map((entry) => entry.maxEntries)),
      instantWinShare: entries.filter((entry) => entry.instantWins > 0).length / entries.length,
    }))
    .sort((a, b) => b.trackedCount - a.trackedCount || a.category.localeCompare(b.category))

  return {
    averageTicketPrice: average(current.map((entry) => entry.ticketPrice)),
    averageMaxEntries: average(current.map((entry) => entry.maxEntries)),
    volume: current.length,
    instantWinShare: current.length === 0 ? 0 : current.filter((entry) => entry.instantWins > 0).length / current.length,
    commonCategories: byCategory.slice(0, 3).map((point) => point.category),
    promotionalPatterns: distinctPatterns(current.map((entry) => entry.promotionalMessaging)),
    byCategory,
  }
}

export function buildPrizeTrends(
  tracked: readonly TrackedCompetition[],
  current: DateWindow,
  previous: DateWindow | null,
): PrizeTrendPoint[] {
  const startedIn = (window: DateWindow) =>
    tracked.filter((competition) => competition.startDate >= window.from && competition.startDate <= window.to)

  const currentByCategory = countByCategory(startedIn(current))
  const previousByCategory = previous ? countByCategory(startedIn(previous)) : null

  const categories = new Set([...currentByCategory.keys(), ...(previousByCategory?.keys() ?? [])])
  return [...categories]
    .map((category) => {
      const trackedCount = currentByCategory.get(category) ?? 0
      const previousCount = previousByCategory?.get(category) ?? 0
      const change = previousByCategory === null || previousCount === 0 ? null : (trackedCount - previousCount) / previousCount
      const direction: PrizeTrendPoint['direction'] =
        previousByCategory === null || trackedCount === previousCount ? 'steady' : trackedCount > previousCount ? 'rising' : 'falling'
      return { category, trackedCount, previousCount, change, direction }
    })
    .sort((a, b) => b.trackedCount - a.trackedCount || a.category.localeCompare(b.category))
}

function countByCategory(competitions: readonly TrackedCompetition[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const competition of competitions) counts.set(competition.category, (counts.get(competition.category) ?? 0) + 1)
  return counts
}

function average(values: readonly number[]): number {
  return values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length
}

/** The messaging strings that recur across at least two competitors, most frequent first. */
function distinctPatterns(messages: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const message of messages) {
    if (!message) continue
    counts.set(message, (counts.get(message) ?? 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([message]) => message)
    .slice(0, 5)
}
